import { memoize, round } from "lodash";
import Big from "big.js";

import { Language } from "../i18n/languages";

export function isPositive(n: number | undefined): boolean {
  return n !== undefined && Math.sign(n) === 1;
}

export function isPositiveOrZero(n: number | undefined): boolean {
  return isPositive(n) || n === 0;
}

export function roundWithDecimals(n: number, decimals: number = 20): number {
  return round(n, decimals);
}

export function percentOf(numerator: number, denominator: number) {
  if (denominator === 0) return 0;
  return Big(numerator).div(denominator).round(4).toNumber();
}

export type FormatPercentOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export const formatPercentOptionsValidated = (options?: FormatPercentOptions) => {
  const max = Math.max(options?.minimumFractionDigits ?? 0, options?.maximumFractionDigits ?? 2);
  const min = Math.min(options?.minimumFractionDigits ?? 2, max);
  return {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  };
};

const numberFormat = memoize((lang: Language) => {
  return new Intl.NumberFormat(lang, { maximumFractionDigits: 20 });
});

export function formatNumber(
  lang: Language,
  input?: number,
  maximumFractionDigits?: number,
): string {
  return input === undefined
    ? ""
    : maximumFractionDigits === undefined
      ? numberFormat(lang).format(input)
      : input.toLocaleString(lang, { maximumFractionDigits: maximumFractionDigits });
}

const percentFormatMemoKey = (locale: Language, options?: FormatPercentOptions) => {
  const option = formatPercentOptionsValidated(options);
  return `${locale}-min${option.minimumFractionDigits}-max${option.maximumFractionDigits}`;
};

const percentFormat = memoize((locale: Language, options?: FormatPercentOptions) => {
  const option = formatPercentOptionsValidated(options);
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: option.minimumFractionDigits,
    maximumFractionDigits: option.maximumFractionDigits,
  });
}, percentFormatMemoKey);

export function formatPercent(
  locale: Language,
  value: number | undefined,
  options?: FormatPercentOptions,
): string {
  return value === undefined ? "" : percentFormat(locale, options).format(value);
}

export function toNumber(x: unknown): number | undefined;
export function toNumber(x: unknown, defaultValue: number): number;
export function toNumber(x: unknown, defaultValue: undefined): number | undefined;
export function toNumber(x: unknown, defaultValue?: number): number | undefined {
  if (!x) {
    return defaultValue;
  }
  const n = Number(x);
  return Number.isNaN(n) ? defaultValue : n;
}
